import React, { useState, useMemo } from 'react';
import { AmountInput } from '../common/AmountInput';
import { todayIso } from '../../utils/accountFilters';
import {
  Percent,
  Plus,
  Filter,
  CheckCircle2,
  AlertTriangle,
  Clock,
  UserCheck,
  Shield,
  Layers,
  Sparkles,
  Calculator,
  Search,
  ArrowRight,
  TrendingUp,
  Download,
  Building,
  RotateCcw,
  History,
  Tag,
  ChevronRight,
  Check,
  X,
  FileSpreadsheet,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import {
  CommissionRule,
  UserCommissionAssignment,
  QDType,
  RecoveryStage,
  CommissionPriorityLevel,
  CommissionAuditLog,
} from '../../types';
import { formatINR } from '../../utils/watermark';
import {
  activeRecoveries,
  inMonth,
  sumCollected,
  currentMonthKey,
  formatMonthLabel,
} from '../../utils/collections';

import { todayLocal } from '../../utils/dates';
import { userInBranch } from '../../utils/branchMatch';
const ALL_QD_TYPES: (QDType | 'All QDs')[] = [
  'All QDs',
  'SMA-0 (0-30 DPD)',
  'SMA-1 (31-60 DPD)',
  'SMA-2 (61-90 DPD)',
  'Critical NPA (>90 DPD)',
  'OTS Schemes',
  'Special Recovery Drive',
];

const ALL_RECOVERY_STAGES: (RecoveryStage | 'All Stages')[] = [
  'All Stages',
  'Early Stage (0-30 DPD)',
  'Mid Stage (31-90 DPD)',
  'Late Stage / Hardcore NPA',
  'OTS Token (10%)',
  'OTS Full Settlement',
  'Partial Recovery',
  'Full Settlement / Closed',
];

// Names reach this app from several places: bulk imports, hand typing, and
// the older build. The same branch therefore arrives as "GARKHEDA" on one row
// and "Garkheda" on the next. Every name comparison on this screen goes
// through here so those two count as one place rather than two.
const normaliseName = (value?: string | null): string =>
  (value || '').trim().replace(/\s+/g, ' ').toLowerCase();

// Keeps the first spelling seen for a name and drops later ones that differ
// only in case or spacing, so a dropdown never offers the same branch twice.
const distinctNames = (values: (string | undefined)[]): string[] => {
  const seen = new Map<string, string>();
  values.forEach((value) => {
    const key = normaliseName(value);
    if (!key) return;
    if (!seen.has(key)) seen.set(key, (value || '').trim().replace(/\s+/g, ' '));
  });
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
};

// A blank bank, zone or branch on a record means "not classified yet", not
// "belongs to no one". The rest of the app keeps showing those rows whatever
// the hierarchy filter says, so they are let through here too instead of
// quietly disappearing the moment someone picks a bank.
const matchesHierarchy = (recordValue: string | undefined, selected: string): boolean => {
  if (!selected) return true;
  const value = normaliseName(recordValue);
  if (!value) return true;
  return value === normaliseName(selected);
};

export const CommissionManagementSection: React.FC = () => {
  const {
    currentUser,
    accounts,
    banks,
    zones,
    branches,
    recoveryDepartments,
    users,
    commissionRules,
    userCommissionAssignments,
    commissionAuditLogs,
    scopedRules,
    scopedAssignments,
    scopedCommissions,
    scopedRecoveries,
    addCommissionRule,
    updateCommissionRule,
    deactivateCommissionRule,
    addUserCommissionAssignment,
    updateUserCommissionAssignment,
    deactivateUserCommissionAssignment,
    calculateCommission,
    commissionSettings,
    updateCommissionRate,
  } = useSRMS();

  // Active subtab: 'rules' | 'assignments' | 'calculator' | 'ledger' | 'audit'
  const [activeTab, setActiveTab] = useState<'rules' | 'assignments' | 'calculator' | 'ledger' | 'audit'>('rules');

  // Filters
  const [filterBank, setFilterBank] = useState<string>('ALL');
  const [filterZone, setFilterZone] = useState<string>('ALL');
  const [filterQD, setFilterQD] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // The engine screen had no month filter at all, so its figures were
  // all-time while every dashboard tile beside it showed the current month.
  // The initialiser runs each time the screen is opened, so the month always
  // starts on the current one rather than on whatever was picked last time.
  const [engineMonth, setEngineMonth] = useState<string>(() => currentMonthKey());

  // Ledger tab filters. Blank means "all", which is why the first option of
  // each selector carries an empty value.
  const [ledgerBank, setLedgerBank] = useState<string>('');
  const [ledgerZone, setLedgerZone] = useState<string>('');
  const [ledgerBranch, setLedgerBranch] = useState<string>('');
  const [ledgerAgent, setLedgerAgent] = useState<string>('');

  // Modals / forms
  const [isAddRuleOpen, setIsAddRuleOpen] = useState<boolean>(false);
  const [ruleName, setRuleName] = useState<string>('');
  const [ruleBank, setRuleBank] = useState<string>('All Banks');
  const [ruleZone, setRuleZone] = useState<string>('All Zones');
  const [ruleDept, setRuleDept] = useState<string>('All Departments');
  const [ruleBranch, setRuleBranch] = useState<string>('All Branches');
  const [ruleQD, setRuleQD] = useState<QDType | 'All QDs'>('All QDs');
  const [ruleStage, setRuleStage] = useState<RecoveryStage | 'All Stages'>('All Stages');
  const [rulePercentage, setRulePercentage] = useState<number>(10);
  const [ruleEffectiveFrom, setRuleEffectiveFrom] = useState<string>(todayIso());
  const [ruleEffectiveTo, setRuleEffectiveTo] = useState<string>('');
  const [ruleDescription, setRuleDescription] = useState<string>('');
  const [ruleFormError, setRuleFormError] = useState<string | null>(null);

  // User Assignment Form
  const [isAddAssignmentOpen, setIsAddAssignmentOpen] = useState<boolean>(false);
  const [assignmentAgentId, setAssignmentAgentId] = useState<string>('');
  const [assignmentPercentage, setAssignmentPercentage] = useState<number>(12);
  const [assignmentBank, setAssignmentBank] = useState<string>('');
  const [assignmentZone, setAssignmentZone] = useState<string>('');
  const [assignmentBranch, setAssignmentBranch] = useState<string>('');
  const [assignmentEffectiveFrom, setAssignmentEffectiveFrom] = useState<string>(todayIso());
  const [assignmentEffectiveTo, setAssignmentEffectiveTo] = useState<string>('');
  const [assignmentRemarks, setAssignmentRemarks] = useState<string>('');
  const [assignmentFormError, setAssignmentFormError] = useState<string | null>(null);

  // Live Calculator Simulator State
  const [simAmount, setSimAmount] = useState<number>(50000);
  const [simAgentId, setSimAgentId] = useState<string>(users.find((u) => u.role === 'agent')?.agentId || users.find((u) => u.role === 'agent')?.id || users[0]?.id || '');
  const [simBank, setSimBank] = useState<string>('State Bank of India');
  const [simZone, setSimZone] = useState<string>('Maharashtra North Zone');
  const [simBranch, setSimBranch] = useState<string>('Pune Main SME Recovery');
  const [simQD, setSimQD] = useState<QDType>('Critical NPA (>90 DPD)');
  const [simStage, setSimStage] = useState<RecoveryStage>('Partial Recovery');

  // Filtered Rules
  const displayedRules = useMemo(() => {
    return scopedRules.filter((r) => {
      const matchesSearch =
        r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.code && r.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        r.bankName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBank = filterBank === 'ALL' || r.bankName === filterBank || r.bankName === 'All Banks';
      const matchesZone = filterZone === 'ALL' || r.zoneName === filterZone || r.zoneName === 'All Zones';
      const matchesQD = filterQD === 'ALL' || r.qdType === filterQD || r.qdType === 'All QDs';
      const matchesStatus = filterStatus === 'ALL' || r.status === filterStatus;
      return matchesSearch && matchesBank && matchesZone && matchesQD && matchesStatus;
    });
  }, [scopedRules, searchTerm, filterBank, filterZone, filterQD, filterStatus]);

  // Filtered Assignments
  const displayedAssignments = useMemo(() => {
    return scopedAssignments.filter((a) => {
      const matchesSearch =
        a.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.agentId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'ALL' || a.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [scopedAssignments, searchTerm, filterStatus]);

  // Simulation Calculation Result
  const simulationResult = useMemo(() => {
    const selectedAgent = users.find((u) => u.agentId === simAgentId || u.id === simAgentId);
    return calculateCommission({
      eligibleAmount: simAmount,
      recoveryDate: todayIso(),
      agentId: simAgentId,
      bankName: simBank || selectedAgent?.bank,
      zoneName: simZone || selectedAgent?.zone,
      branchName: simBranch || selectedAgent?.branch,
      qdType: simQD,
      recoveryStage: simStage,
    });
  }, [simAmount, simAgentId, simBank, simZone, simBranch, simQD, simStage, calculateCommission, users]);

  // Summary Metrics
  const activeRulesCount = scopedRules.filter((r) => r.status === 'active').length;
  const activeAssignmentsCount = scopedAssignments.filter((a) => a.status === 'active').length;
  // Both tiles now cover the same set of collections: the selected month,
  // reversals excluded. Previously "Total Recoveries Processed" counted every
  // month and kept counting money that had been reversed, while
  // "Commissions Calculated" dropped reversed rows because reverseRecovery
  // deletes the commission record -- so the two tiles could never agree.
  const ledgerRecoveries = useMemo(
    () => inMonth(activeRecoveries(scopedRecoveries), engineMonth),
    [scopedRecoveries, engineMonth]
  );
  const ledgerCommissions = useMemo(() => {
    if (engineMonth === 'all') return scopedCommissions;
    return scopedCommissions.filter(
      (c) => (c.date || '').slice(0, 7) === engineMonth
    );
  }, [scopedCommissions, engineMonth]);

  const totalCommissionEarned = ledgerCommissions.reduce(
    (sum, c) => sum + c.commissionAmount,
    0
  );
  const totalRecoveryProcessed = sumCollected(ledgerRecoveries);

  const engineMonthOptions = useMemo(() => {
    const set = new Set<string>();
    scopedRecoveries.forEach((r) => {
      const k = (r.recoveryDate || '').slice(0, 7);
      if (k.length === 7) set.add(k);
    });
    set.add(currentMonthKey());
    return Array.from(set).sort().reverse();
  }, [scopedRecoveries]);

  // Every bank / zone / branch combination the app already holds in memory.
  // The dropdowns below are built only from this, because a lookup call per
  // selector would add database reads to every visit to this tab and reads
  // are what this app is billed for.
  const ledgerHierarchyRows = useMemo(() => {
    const rows: { bank?: string; zone?: string; branch?: string }[] = [];
    scopedCommissions.forEach((c) => rows.push({ bank: c.bank, zone: c.zone, branch: c.branch }));
    scopedRecoveries.forEach((r) => rows.push({ bank: r.bank, zone: r.zone, branch: r.branch }));
    accounts.forEach((a) => rows.push({ bank: a.bank, zone: a.zone, branch: a.branch }));
    // Masters fill the gaps: a branch nobody has recovered against yet still
    // belongs under its bank and zone, and should still be selectable.
    branches.forEach((br) => rows.push({ bank: br.bankName, zone: br.zoneName || br.zone, branch: br.name }));
    zones.forEach((z) => rows.push({ bank: z.bankName, zone: z.name }));
    return rows;
  }, [scopedCommissions, scopedRecoveries, accounts, branches, zones]);

  const ledgerBankOptions = useMemo(
    () =>
      distinctNames([
        ...ledgerHierarchyRows.map((row) => row.bank),
        ...banks.map((b) => b.name),
      ]),
    [ledgerHierarchyRows, banks]
  );

  // Each selector below is narrowed by the ones above it, so picking a bank
  // leaves only that bank's zonal offices to choose from.
  const ledgerZoneOptions = useMemo(
    () =>
      distinctNames(
        ledgerHierarchyRows
          .filter((row) => matchesHierarchy(row.bank, ledgerBank))
          .map((row) => row.zone)
      ),
    [ledgerHierarchyRows, ledgerBank]
  );

  const ledgerBranchOptions = useMemo(
    () =>
      distinctNames(
        ledgerHierarchyRows
          .filter((row) => matchesHierarchy(row.bank, ledgerBank) && matchesHierarchy(row.zone, ledgerZone))
          .map((row) => row.branch)
      ),
    [ledgerHierarchyRows, ledgerBank, ledgerZone]
  );

  // Agents come from the staff list and from the ledger itself, so someone who
  // has left but was paid commission in an earlier month can still be picked.
  const ledgerAgentOptions = useMemo(() => {
    const found = new Map<string, string>();
    const add = (key?: string, name?: string) => {
      const id = (key || '').trim();
      if (!id) return;
      if (!found.has(id)) found.set(id, (name || '').trim() || id);
    };
    users
      .filter((u) => {
        if (u.role !== 'agent') return false;
        if (!ledgerBranch) return true;
        // Nothing recorded about where they work: shown as before, rather
        // than quietly disappearing.
        const placed =
          !!u.branch ||
          ((u.branches as string[] | undefined) || []).length > 0 ||
          !!(u as any).branchId ||
          (((u as any).branchIds as string[] | undefined) || []).length > 0;
        if (!placed) return true;
        // Branch id first, then name AND bank: two banks can both have a
        // branch called Bidkin. See src/utils/branchMatch.ts.
        return userInBranch(u, ledgerBranch, ledgerBank, branches);
      })
      .forEach((u) => add(u.agentId || u.id, u.name));
    scopedCommissions
      .filter((c) => matchesHierarchy(c.branch, ledgerBranch))
      .forEach((c) => add(c.agentId, c.agentName));
    return Array.from(found.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, scopedCommissions, ledgerBranch, ledgerBank, branches]);

  const selectedLedgerAgentName = useMemo(
    () => ledgerAgentOptions.find((a) => a.id === ledgerAgent)?.name || '',
    [ledgerAgentOptions, ledgerAgent]
  );

  // The table below used to print every commission ever recorded while the
  // tiles above it covered a single month, so the two could never agree. It
  // now starts from the same month-scoped set and then applies the selectors.
  const displayedCommissions = useMemo(() => {
    return ledgerCommissions.filter((c) => {
      if (!matchesHierarchy(c.bank, ledgerBank)) return false;
      if (!matchesHierarchy(c.zone, ledgerZone)) return false;
      if (!matchesHierarchy(c.branch, ledgerBranch)) return false;
      if (ledgerAgent) {
        // Asking for one agent is a question about one person, so unlike the
        // hierarchy filters a record that names nobody is not let through.
        // The name is checked as well as the code because older rows were
        // written before every agent carried a staff code.
        const matchesId = normaliseName(c.agentId) === normaliseName(ledgerAgent);
        const matchesAgentName =
          !!selectedLedgerAgentName && normaliseName(c.agentName) === normaliseName(selectedLedgerAgentName);
        if (!matchesId && !matchesAgentName) return false;
      }
      return true;
    });
  }, [ledgerCommissions, ledgerBank, ledgerZone, ledgerBranch, ledgerAgent, selectedLedgerAgentName]);

  const displayedCommissionTotal = displayedCommissions.reduce(
    (sum, c) => sum + c.commissionAmount,
    0
  );

  const hasLedgerFilters = Boolean(ledgerBank || ledgerZone || ledgerBranch || ledgerAgent);

  const clearLedgerFilters = () => {
    setLedgerBank('');
    setLedgerZone('');
    setLedgerBranch('');
    setLedgerAgent('');
  };

  // Form Handlers
  const handleCreateRule = (e: React.FormEvent) => {
    e.preventDefault();
    setRuleFormError(null);

    const res = addCommissionRule({
      name: ruleName,
      bankName: ruleBank,
      zoneName: ruleZone,
      departmentName: ruleDept,
      branchName: ruleBranch,
      qdType: ruleQD,
      recoveryStage: ruleStage,
      commissionPercentage: Number(rulePercentage),
      effectiveFrom: ruleEffectiveFrom,
      effectiveTo: ruleEffectiveTo || undefined,
      description: ruleDescription,
      status: 'active',
    });

    if (!res.success) {
      setRuleFormError(res.message);
      return;
    }

    setIsAddRuleOpen(false);
    setRuleName('');
    setRuleDescription('');
  };

  const handleCreateAssignment = (e: React.FormEvent) => {
    e.preventDefault();
    setAssignmentFormError(null);

    const agent = users.find((u) => u.id === assignmentAgentId || u.agentId === assignmentAgentId);
    if (!agent) {
      setAssignmentFormError('Please select a valid recovery agent.');
      return;
    }

    const res = addUserCommissionAssignment({
      agentId: agent.agentId || agent.id,
      agentName: agent.name,
      percentageOverride: Number(assignmentPercentage),
      bankName: assignmentBank || agent.bank,
      zoneName: assignmentZone || agent.zone,
      branchName: assignmentBranch || agent.branch,
      effectiveFrom: assignmentEffectiveFrom,
      effectiveTo: assignmentEffectiveTo || undefined,
      remarks: assignmentRemarks,
      status: 'active',
    });

    if (!res.success) {
      setAssignmentFormError(res.message);
      return;
    }

    setIsAddAssignmentOpen(false);
    setAssignmentRemarks('');
  };

  // Export Commission Ledger to CSV
  const handleExportCommissionCSV = () => {
    const headers = [
      'Commission ID',
      'Agent Name',
      'Agent ID',
      'Recovery ID',
      'Account ID',
      'Recovery Amount (INR)',
      'Commission Rate (%)',
      'Commission Amount (INR)',
      'Date',
      'Status',
      'Priority Level',
      'Priority Description',
      'Bank',
      'Zone',
      'Branch',
      'QD Type',
      'Recovery Stage',
    ];

    const rows = scopedCommissions.map((c) => [
      c.id,
      `"${c.agentName}"`,
      c.agentId,
      c.recoveryId,
      c.accountId,
      c.recoveryAmount,
      `${c.commissionRate}%`,
      c.commissionAmount,
      c.date,
      c.status,
      c.priorityLevel || 'Default Rate',
      `"${c.priorityDescription || ''}"`,
      `"${c.bank || ''}"`,
      `"${c.zone || ''}"`,
      `"${c.branch || ''}"`,
      `"${c.qdType || ''}"`,
      `"${c.recoveryStage || ''}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Commission_Ledger_Export_${todayLocal()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPriorityBadge = (p?: CommissionPriorityLevel) => {
    switch (p) {
      case 1:
        return <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded font-bold text-[10px] border border-purple-200">Priority 1: Agent Override</span>;
      case 2:
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-bold text-[10px] border border-blue-200">Priority 2: Bank+Zone+Branch+QD</span>;
      case 3:
        return <span className="px-2 py-0.5 bg-cyan-100 text-cyan-800 rounded font-bold text-[10px] border border-cyan-200">Priority 3: Bank+Zone+QD</span>;
      case 4:
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-bold text-[10px] border border-amber-200">Priority 4: Bank+Zone</span>;
      case 5:
        return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px] border border-emerald-200">Priority 5: Bank Default</span>;
      default:
        return <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-bold text-[10px] border border-slate-200">System Default</span>;
    }
  };

  return (
    <div className="space-y-5 text-slate-800">
      {/* Top Header & Metrics Banner */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center border border-blue-200 font-bold">
              <Percent className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span>Commission Management Engine</span>
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-mono text-[10px] font-bold rounded-full border border-emerald-200">
                  Priority 1-5 Engine
                </span>
              </h2>
              <p className="text-xs text-slate-500">
                User-wise commission rates, hierarchical rule priorities (Bank → Zone → Branch → QD), and immutable transaction records
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportCommissionCSV}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-xs flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export Ledger (.csv)</span>
            </button>
            {currentUser.role === 'admin' && (
              <>
                <button
                  onClick={() => setIsAddAssignmentOpen(true)}
                  className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-xs transition cursor-pointer"
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  <span>Assign Agent Rate</span>
                </button>
                <button
                  onClick={() => setIsAddRuleOpen(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-xs transition cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Hierarchy Rule</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Month selector -- drives the two collection tiles below */}
        <div className="flex flex-wrap items-center gap-2 pt-4">
          <span className="text-[11px] font-semibold text-slate-600">
            Showing figures for:
          </span>
          <select
            value={engineMonth}
            onChange={(e) => setEngineMonth(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="all">All Months (Overall)</option>
            {engineMonthOptions.map((m) => (
              <option key={m} value={m}>
                {formatMonthLabel(m)}
              </option>
            ))}
          </select>
        </div>

        {/* 4 Metric Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
            <span className="text-[11px] text-slate-500 font-medium">Active Hierarchy Rules</span>
            <div className="text-lg font-black text-slate-900 font-mono">{activeRulesCount}</div>
            <span className="text-[10px] text-blue-600 font-semibold">Priority Levels 2 to 5</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
            <span className="text-[11px] text-slate-500 font-medium">Agent Specific Overrides</span>
            <div className="text-lg font-black text-purple-700 font-mono">{activeAssignmentsCount}</div>
            <span className="text-[10px] text-purple-600 font-semibold">Priority 1 Overrides</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
            <span className="text-[11px] text-slate-500 font-medium">Total Recoveries Processed</span>
            <div className="text-lg font-black text-emerald-700 font-mono">{formatINR(totalRecoveryProcessed)}</div>
            <span className="text-[10px] text-slate-500">{ledgerRecoveries.length} Transactions</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
            <span className="text-[11px] text-slate-500 font-medium">Commissions Calculated</span>
            <div className="text-lg font-black text-amber-700 font-mono">{formatINR(totalCommissionEarned)}</div>
            <span className="text-[10px] text-amber-600 font-semibold">Fully Reconciled</span>
          </div>
        </div>
      </div>

      {/* Navigation Pills */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
          {[
            { id: 'rules', label: 'Hierarchy Commission Rules', count: scopedRules.length, icon: Layers },
            { id: 'assignments', label: 'Agent Specific Assignments', count: scopedAssignments.length, icon: UserCheck },
            { id: 'calculator', label: 'Priority Calculator & Simulator', icon: Calculator },
            { id: 'ledger', label: 'Transaction Commission Ledger', count: scopedCommissions.length, icon: History },
            { id: 'audit', label: 'Audit Trail', count: commissionAuditLogs.length, icon: Shield },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3 py-2 rounded-lg transition cursor-pointer flex items-center gap-2 ${
                  isActive
                    ? 'bg-white text-blue-700 shadow-xs font-bold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      isActive ? 'bg-blue-100 text-blue-800 font-bold' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search input for rules & assignments */}
        {(activeTab === 'rules' || activeTab === 'assignments' || activeTab === 'ledger') && (
          <div className="relative min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search rule, bank, or agent..."
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* 1. HIERARCHY COMMISSION RULES TAB */}
      {/* ======================================================== */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
              <Filter className="w-3.5 h-3.5" />
              <span>Filters:</span>
            </div>

            <select
              value={filterBank}
              onChange={(e) => setFilterBank(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Banks</option>
              {banks.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>

            <select
              value={filterZone}
              onChange={(e) => setFilterZone(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Zones</option>
              {zones.map((z) => (
                <option key={z.id} value={z.name}>
                  {z.name}
                </option>
              ))}
            </select>

            <select
              value={filterQD}
              onChange={(e) => setFilterQD(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All QD Types</option>
              {ALL_QD_TYPES.map((qd) => (
                <option key={qd} value={qd}>
                  {qd}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>

            {(filterBank !== 'ALL' || filterZone !== 'ALL' || filterQD !== 'ALL' || filterStatus !== 'ALL' || searchTerm) && (
              <button
                onClick={() => {
                  setFilterBank('ALL');
                  setFilterZone('ALL');
                  setFilterQD('ALL');
                  setFilterStatus('ALL');
                  setSearchTerm('');
                }}
                className="text-blue-600 hover:text-blue-800 font-semibold ml-auto flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Reset</span>
              </button>
            )}
          </div>

          {/* Rules Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="p-3.5">Priority &amp; Code</th>
                    <th className="p-3.5">Rule Name</th>
                    <th className="p-3.5">Hierarchy Scope (Bank / Zone / Branch)</th>
                    <th className="p-3.5">QD Type &amp; Stage</th>
                    <th className="p-3.5 text-right">Commission Rate</th>
                    <th className="p-3.5">Effective Dates</th>
                    <th className="p-3.5 text-center">Status</th>
                    {currentUser.role === 'admin' && <th className="p-3.5 text-right">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {displayedRules.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        No commission rules match your selected filters.
                      </td>
                    </tr>
                  ) : (
                    displayedRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-slate-50 transition">
                        <td className="p-3.5">
                          <div className="space-y-1">
                            {getPriorityBadge(rule.priorityLevel)}
                            <span className="block font-mono text-[10px] text-slate-500 font-semibold">{rule.code}</span>
                          </div>
                        </td>
                        <td className="p-3.5">
                          <div className="font-bold text-slate-900">{rule.name}</div>
                          {rule.description && <p className="text-[11px] text-slate-500">{rule.description}</p>}
                        </td>
                        <td className="p-3.5 text-[11px] space-y-0.5">
                          <div className="font-semibold text-slate-800 flex items-center gap-1">
                            <Building className="w-3 h-3 text-slate-400" />
                            <span>{rule.bankName}</span>
                          </div>
                          <div className="text-slate-500">{rule.zoneName} • {rule.branchName}</div>
                          {rule.departmentName && rule.departmentName !== 'All Departments' && (
                            <span className="inline-block px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[9px]">
                              {rule.departmentName}
                            </span>
                          )}
                        </td>
                        <td className="p-3.5 text-[11px]">
                          <span className="font-medium text-purple-700 block">{rule.qdType}</span>
                          <span className="text-slate-500 text-[10px]">{rule.recoveryStage}</span>
                        </td>
                        <td className="p-3.5 text-right font-mono font-black text-sm text-emerald-600">
                          {rule.commissionPercentage}%
                        </td>
                        <td className="p-3.5 text-[11px] text-slate-600">
                          <div>From: {rule.effectiveFrom}</div>
                          {rule.effectiveTo ? <div className="text-slate-500">To: {rule.effectiveTo}</div> : <span className="text-emerald-600 text-[10px]">Ongoing</span>}
                        </td>
                        <td className="p-3.5 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                              rule.status === 'active'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-slate-100 text-slate-600 border border-slate-300'
                            }`}
                          >
                            {rule.status}
                          </span>
                        </td>
                        {currentUser.role === 'admin' && (
                          <td className="p-3.5 text-right">
                            {rule.status === 'active' ? (
                              <button
                                onClick={() => deactivateCommissionRule(rule.id)}
                                className="px-2.5 py-1 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold border border-red-200 transition cursor-pointer"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => updateCommissionRule(rule.id, { status: 'active' })}
                                className="px-2.5 py-1 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs font-semibold border border-emerald-200 transition cursor-pointer"
                              >
                                Activate
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. AGENT SPECIFIC ASSIGNMENTS TAB */}
      {/* ======================================================== */}
      {activeTab === 'assignments' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl flex items-center justify-between text-xs text-purple-900">
            <div className="flex items-center gap-3">
              <UserCheck className="w-5 h-5 text-purple-600 shrink-0" />
              <div>
                <span className="font-bold block">Priority 1: Agent Specific Direct Override</span>
                <span className="text-purple-700">
                  When a Recovery Agent has an active override configured here, this percentage is applied directly before evaluating Bank, Zone, Branch, or QD rules.
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="p-3.5">Agent Details</th>
                    <th className="p-3.5">Assigned Hierarchy Scope</th>
                    <th className="p-3.5 text-right">Commission Rate Override</th>
                    <th className="p-3.5">Effective Dates</th>
                    <th className="p-3.5">Remarks / Rationale</th>
                    <th className="p-3.5 text-center">Status</th>
                    {currentUser.role === 'admin' && <th className="p-3.5 text-right">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {displayedAssignments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-500">
                        No agent-specific commission overrides found.
                      </td>
                    </tr>
                  ) : (
                    displayedAssignments.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50 transition">
                        <td className="p-3.5">
                          <div className="font-bold text-slate-900">{a.agentName}</div>
                          <span className="font-mono text-purple-700 text-[11px] font-semibold">{a.agentId}</span>
                        </td>
                        <td className="p-3.5 text-[11px] text-slate-600">
                          <div>{a.bankName || 'All Banks'}</div>
                          <div className="text-slate-500">{a.zoneName || 'All Zones'} • {a.branchName || 'All Branches'}</div>
                        </td>
                        <td className="p-3.5 text-right font-mono font-black text-sm text-purple-700">
                          {a.percentageOverride}%
                        </td>
                        <td className="p-3.5 text-[11px] text-slate-600">
                          <div>From: {a.effectiveFrom}</div>
                          {a.effectiveTo ? <div>To: {a.effectiveTo}</div> : <span className="text-emerald-600 text-[10px]">Ongoing</span>}
                        </td>
                        <td className="p-3.5 text-slate-600 italic max-w-xs">{a.remarks || 'Standard assignment'}</td>
                        <td className="p-3.5 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                              a.status === 'active'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-slate-100 text-slate-600 border border-slate-300'
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        {currentUser.role === 'admin' && (
                          <td className="p-3.5 text-right">
                            {a.status === 'active' ? (
                              <button
                                onClick={() => deactivateUserCommissionAssignment(a.id)}
                                className="px-2.5 py-1 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold border border-red-200 transition cursor-pointer"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => updateUserCommissionAssignment(a.id, { status: 'active' })}
                                className="px-2.5 py-1 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs font-semibold border border-emerald-200 transition cursor-pointer"
                              >
                                Activate
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 3. PRIORITY CALCULATOR & SIMULATOR TAB */}
      {/* ======================================================== */}
      {activeTab === 'calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Inputs Column */}
          <div className="lg:col-span-6 bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4 text-xs">
            <div className="border-b border-slate-100 pb-3 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-blue-600" />
              <h3 className="font-bold text-sm text-slate-900">Commission Rule Priority Simulator</h3>
            </div>
            <p className="text-slate-500">
              Test any recovery transaction against the 5-tier priority hierarchy engine in real-time.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Eligible Recovery Amount (₹):</label>
                <AmountInput
                  value={simAmount}
                  onValueChange={(n) => setSimAmount(Math.max(0, n))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono font-bold text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Select Recovery Agent (Credited):</label>
                <select
                  value={simAgentId}
                  onChange={(e) => setSimAgentId(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {users.filter((u) => u.role === 'agent').map((u) => (
                    <option key={u.id} value={u.agentId || u.id}>
                      {u.name} ({u.agentId || u.id}) - {u.branch || 'Branch'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Bank Name:</label>
                  <select
                    value={simBank}
                    onChange={(e) => setSimBank(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {banks.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Zone:</label>
                  <select
                    value={simZone}
                    onChange={(e) => setSimZone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {zones.map((z) => (
                      <option key={z.id} value={z.name}>{z.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Branch:</label>
                  <select
                    value={simBranch}
                    onChange={(e) => setSimBranch(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {branches.map((br) => (
                      <option key={br.id} value={br.name}>{br.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">QD Classification:</label>
                  <select
                    value={simQD}
                    onChange={(e) => setSimQD(e.target.value as QDType)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ALL_QD_TYPES.filter((q) => q !== 'All QDs').map((qd) => (
                      <option key={qd} value={qd}>{qd}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Recovery Stage:</label>
                <select
                  value={simStage}
                  onChange={(e) => setSimStage(e.target.value as RecoveryStage)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ALL_RECOVERY_STAGES.filter((s) => s !== 'All Stages').map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Column */}
          <div className="lg:col-span-6 bg-slate-900 text-white rounded-2xl p-5 shadow-lg space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="font-bold text-sm">Engine Resolution Output</span>
                </div>
                {getPriorityBadge(simulationResult.priorityLevel)}
              </div>

              {simulationResult.conflictDetected && (
                <div className="p-3 bg-red-900/60 border border-red-500 rounded-xl text-red-200 text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <span>Duplicate commission rule detected. Please resolve the conflicting commission rules.</span>
                </div>
              )}

              {/* Amount and Rate Highlighting */}
              <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-slate-400 text-xs font-medium">Applied Commission Rate:</span>
                  <span className="font-mono text-3xl font-black text-emerald-400">
                    {simulationResult.appliedPercentage}%
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-t border-slate-700/80 pt-2">
                  <span className="text-slate-400 text-xs font-medium">Calculated Commission Payout:</span>
                  <span className="font-mono text-2xl font-black text-amber-400">
                    {formatINR(simulationResult.commissionAmount)}
                  </span>
                </div>
              </div>

              {/* Priority Ladder Step-by-Step Visualization */}
              <div className="space-y-2">
                <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">
                  Priority Evaluation Ladder:
                </span>
                <div className="space-y-1.5 text-xs font-mono">
                  {[
                    { level: 1, name: '1. User/Agent Specific Override', matched: simulationResult.priorityLevel === 1 },
                    { level: 2, name: '2. Bank + Zone + Branch + QD Type', matched: simulationResult.priorityLevel === 2 },
                    { level: 3, name: '3. Bank + Zone + QD Type', matched: simulationResult.priorityLevel === 3 },
                    { level: 4, name: '4. Bank + Zone Broad Rule', matched: simulationResult.priorityLevel === 4 },
                    { level: 5, name: '5. Bank Default Rate', matched: simulationResult.priorityLevel === 5 },
                  ].map((tier) => (
                    <div
                      key={tier.level}
                      className={`p-2.5 rounded-lg border flex items-center justify-between ${
                        tier.matched
                          ? 'bg-blue-600/30 border-blue-500 text-white font-bold'
                          : 'bg-slate-800/40 border-slate-800 text-slate-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {tier.matched ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />}
                        <span>{tier.name}</span>
                      </div>
                      {tier.matched && <span className="text-emerald-400 text-[11px]">MATCHED &amp; APPLIED</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Rationale description */}
              <div className="p-3 bg-slate-800 rounded-xl border border-slate-700 text-xs space-y-1">
                <span className="text-slate-400 font-semibold block">Resolution Explanation:</span>
                <p className="text-slate-200 italic">{simulationResult.priorityDescription}</p>
                {simulationResult.appliedRuleName && (
                  <span className="text-blue-400 block text-[11px] font-mono">
                    Rule: {simulationResult.appliedRuleName} ({simulationResult.appliedRuleId})
                  </span>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
              <span>Simulation uses live in-memory &amp; persisted rule sets.</span>
              <span className="text-emerald-400 font-bold">100% Deterministic</span>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. TRANSACTION COMMISSION LEDGER TAB */}
      {/* ======================================================== */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
            <div>
              <h3 className="font-bold text-sm text-slate-900">Finalized Recovery Commission Ledger</h3>
              <p className="text-slate-500">
                Immutable commission records credited upon each recovery receipt creation with full rule audit snapshot.
              </p>
            </div>
            <div className="font-mono text-right">
              <span className="text-slate-500 block text-[11px]">Total Reconciled Payout:</span>
              {/* Follows the filters below, so this figure and the rows on
                  screen always describe the same set of receipts. */}
              <span className="text-emerald-700 font-black text-base">{formatINR(displayedCommissionTotal)}</span>
            </div>
          </div>

          {/* Ledger filter row. Every option comes from records the app has
              already loaded, so opening this tab costs no extra reads. */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
              <Filter className="w-3.5 h-3.5" />
              <span>Filters:</span>
            </div>

            <select
              value={ledgerBank}
              onChange={(e) => {
                // Picking a new bank makes the choices below it meaningless,
                // so they are cleared rather than left pointing elsewhere.
                setLedgerBank(e.target.value);
                setLedgerZone('');
                setLedgerBranch('');
                setLedgerAgent('');
              }}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Banks</option>
              {ledgerBankOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select
              value={ledgerZone}
              onChange={(e) => {
                setLedgerZone(e.target.value);
                setLedgerBranch('');
                setLedgerAgent('');
              }}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Zonal Offices</option>
              {ledgerZoneOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select
              value={ledgerBranch}
              onChange={(e) => {
                setLedgerBranch(e.target.value);
                setLedgerAgent('');
              }}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Branches</option>
              {ledgerBranchOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select
              value={ledgerAgent}
              onChange={(e) => setLedgerAgent(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Agents</option>
              {ledgerAgentOptions.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.id})
                </option>
              ))}
            </select>

            {/* The same month state as the selector at the top of the screen,
                repeated here so the whole filter row can be read in one place
                without scrolling back up. */}
            <select
              value={engineMonth}
              onChange={(e) => setEngineMonth(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Months (Overall)</option>
              {engineMonthOptions.map((m) => (
                <option key={m} value={m}>
                  {formatMonthLabel(m)}
                </option>
              ))}
            </select>

            <div className="ml-auto flex flex-wrap items-center gap-3">
              <span className="text-slate-500">
                Showing{' '}
                <span className="font-mono font-bold text-slate-800">{displayedCommissions.length}</span> of{' '}
                <span className="font-mono font-bold text-slate-800">{scopedCommissions.length}</span> entries
              </span>
              <span className="text-slate-500">
                Commission shown:{' '}
                <span className="font-mono font-bold text-emerald-700">{formatINR(displayedCommissionTotal)}</span>
              </span>
              {hasLedgerFilters && (
                <button
                  onClick={clearLedgerFilters}
                  className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Clear filters</span>
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="p-3.5">Date &amp; Receipt</th>
                    <th className="p-3.5">Credited Agent</th>
                    <th className="p-3.5">Account ID &amp; Bank</th>
                    <th className="p-3.5 text-right">Recovery Amount</th>
                    <th className="p-3.5 text-right">Commission Rate</th>
                    <th className="p-3.5 text-right">Commission Amount</th>
                    <th className="p-3.5">Rule Priority Applied</th>
                    <th className="p-3.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {displayedCommissions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        {scopedCommissions.length === 0
                          ? 'No recovery commission entries recorded yet.'
                          : 'No commission entries match the selected month and filters.'}
                      </td>
                    </tr>
                  ) : (
                    displayedCommissions.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50 transition">
                        <td className="p-3.5">
                          <div className="font-bold text-slate-900">{c.date}</div>
                          <span className="font-mono text-blue-600 text-[10px]">{c.recoveryId}</span>
                        </td>
                        <td className="p-3.5">
                          <div className="font-bold text-slate-900">{c.agentName}</div>
                          <span className="font-mono text-slate-500 text-[10px]">{c.agentId}</span>
                        </td>
                        <td className="p-3.5 text-[11px]">
                          <span className="font-mono font-bold text-slate-800 block">{c.accountId}</span>
                          <span className="text-slate-500">{c.bank || 'Bank'} • {c.branch || 'Branch'}</span>
                        </td>
                        <td className="p-3.5 text-right font-mono font-bold text-slate-900">
                          {formatINR(c.recoveryAmount)}
                        </td>
                        <td className="p-3.5 text-right font-mono font-bold text-purple-700">
                          {c.commissionRate}%
                        </td>
                        <td className="p-3.5 text-right font-mono font-black text-sm text-emerald-600">
                          {formatINR(c.commissionAmount)}
                        </td>
                        <td className="p-3.5">
                          {getPriorityBadge(c.priorityLevel)}
                          {c.appliedRuleName && (
                            <span className="block text-[10px] text-slate-500 mt-0.5">{c.appliedRuleName}</span>
                          )}
                        </td>
                        <td className="p-3.5 text-center">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[10px] border border-emerald-300">
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 5. AUDIT TRAIL TAB */}
      {/* ======================================================== */}
      {activeTab === 'audit' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs text-xs">
          <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-600" />
              <h3 className="font-bold text-sm text-slate-900">Commission Rule &amp; Assignment Audit Trail</h3>
            </div>
            <span className="text-slate-500 font-mono text-[11px]">{commissionAuditLogs.length} total recorded logs</span>
          </div>

          <div className="space-y-3">
            {commissionAuditLogs.length === 0 ? (
              <p className="text-slate-500 text-center py-6">No audit log entries recorded yet.</p>
            ) : (
              commissionAuditLogs.map((log) => (
                <div key={log.id} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          log.action === 'CREATED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : log.action === 'ASSIGNED'
                            ? 'bg-purple-100 text-purple-800'
                            : log.action === 'DEACTIVATED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {log.action}
                      </span>
                      <span className="font-bold text-slate-900">{log.entityName}</span>
                    </div>
                    <span className="text-[11px] text-slate-500 font-mono">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-slate-700">{log.details}</p>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    <span>Authorized by:</span>
                    <span className="font-semibold text-slate-700">{log.performedByName}</span>
                    <span>({log.performedByUserId})</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: CREATE HIERARCHY COMMISSION RULE */}
      {/* ======================================================== */}
      {isAddRuleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-xl shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-600" />
                <span>Create Hierarchical Commission Rule</span>
              </h3>
              <button onClick={() => setIsAddRuleOpen(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateRule} className="p-6 space-y-4 max-h-[85vh] overflow-y-auto">
              {ruleFormError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <span>{ruleFormError}</span>
                </div>
              )}

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Rule Name *:</label>
                <input
                  type="text"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g. SBI Critical NPA Special Recovery Incentive"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Bank Scope:</label>
                  <select
                    value={ruleBank}
                    onChange={(e) => setRuleBank(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All Banks">All Banks</option>
                    {banks.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Zone Scope:</label>
                  <select
                    value={ruleZone}
                    onChange={(e) => setRuleZone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All Zones">All Zones</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.name}>{z.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Recovery Department:</label>
                  <select
                    value={ruleDept}
                    onChange={(e) => setRuleDept(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All Departments">All Departments</option>
                    {recoveryDepartments.map((d) => (
                      <option key={d.id} value={d.name}>{d.name} ({d.bankName})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Branch Scope:</label>
                  <select
                    value={ruleBranch}
                    onChange={(e) => setRuleBranch(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All Branches">All Branches</option>
                    {branches.map((br) => (
                      <option key={br.id} value={br.name}>{br.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">QD Classification:</label>
                  <select
                    value={ruleQD}
                    onChange={(e) => setRuleQD(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ALL_QD_TYPES.map((qd) => (
                      <option key={qd} value={qd}>{qd}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Recovery Stage:</label>
                  <select
                    value={ruleStage}
                    onChange={(e) => setRuleStage(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ALL_RECOVERY_STAGES.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Commission Percentage (0 - 100%) *:</label>
                  <AmountInput
                    value={rulePercentage}
                    onValueChange={setRulePercentage}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono font-bold focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Effective From Date *:</label>
                  <input
                    type="date"
                    value={ruleEffectiveFrom}
                    onChange={(e) => setRuleEffectiveFrom(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Effective To Date (Optional):</label>
                <input
                  type="date"
                  value={ruleEffectiveTo}
                  onChange={(e) => setRuleEffectiveTo(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Description / Policy Circular Reference:</label>
                <textarea
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                  rows={2}
                  placeholder="e.g. As per Bank Recovery Circular No. RC-2026/04 for Q3 stressed asset recovery"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddRuleOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 font-bold rounded-xl text-white shadow-xs cursor-pointer"
                >
                  Create Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ASSIGN AGENT SPECIFIC RATE */}
      {/* ======================================================== */}
      {isAddAssignmentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-purple-600" />
                <span>Assign Agent Commission Rate Override (Priority 1)</span>
              </h3>
              <button onClick={() => setIsAddAssignmentOpen(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAssignment} className="p-6 space-y-4 max-h-[85vh] overflow-y-auto">
              {assignmentFormError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <span>{assignmentFormError}</span>
                </div>
              )}

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Select Recovery Agent *:</label>
                <select
                  value={assignmentAgentId}
                  onChange={(e) => {
                    setAssignmentAgentId(e.target.value);
                    const selected = users.find((u) => u.id === e.target.value || u.agentId === e.target.value);
                    if (selected) {
                      setAssignmentBank(selected.bank || '');
                      setAssignmentZone(selected.zone || '');
                      setAssignmentBranch(selected.branch || '');
                    }
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">-- Choose Agent --</option>
                  {users.filter((u) => u.role === 'agent').map((u) => (
                    <option key={u.id} value={u.agentId || u.id}>
                      {u.name} ({u.agentId || u.id}) - {u.branch || 'Branch'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Override Percentage (0 - 100%) *:</label>
                <AmountInput
                  value={assignmentPercentage}
                  onValueChange={setAssignmentPercentage}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono font-bold focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Effective From Date *:</label>
                  <input
                    type="date"
                    value={assignmentEffectiveFrom}
                    onChange={(e) => setAssignmentEffectiveFrom(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Effective To Date (Optional):</label>
                  <input
                    type="date"
                    value={assignmentEffectiveTo}
                    onChange={(e) => setAssignmentEffectiveTo(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Assignment Remarks / Justification:</label>
                <textarea
                  value={assignmentRemarks}
                  onChange={(e) => setAssignmentRemarks(e.target.value)}
                  rows={2}
                  placeholder="e.g. Senior field agent special target performance override"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddAssignmentOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 font-bold rounded-xl text-white shadow-xs cursor-pointer"
                >
                  Save Override
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
